import {
    type CanonicalUrl,
    isChoiceDeclarationField,
    isProfileTypeSchema,
    type ProfileExtension,
    type SnapshotProfileTypeSchema,
    snapshotIdentifier,
    type TypeIdentifier,
} from "@root/typeschema/types";
import type { TypeSchemaIndex } from "@root/typeschema/utils";
import {
    tsCamelCase,
    tsExtensionFlatTypeName,
    tsProfileClassName,
    tsProfileModuleName,
    tsResourceName,
    tsValueFieldName,
} from "./name";
import { collectProfileFactoryInfo } from "./profile";
import { tsTypeFromIdentifier } from "./utils";
import type { TypeScript } from "./writer";

export type SubExtensionSliceInfo = {
    name: string;
    url: string;
    valueField: string;
    tsType: string;
    isArray: boolean;
    isRequired: boolean;
};

export type ExtensionProfileInfo = {
    className: string;
    modulePath: string;
    snapshot: SnapshotProfileTypeSchema;
};

/**
 * Extract value field name from a slice's `elements` list.
 * E.g. `["url", "value", "valueCoding"]` → `"valueCoding"`
 */
export const extractValueField = (elements: string[] | undefined): string | undefined => {
    if (!elements) return undefined;
    return elements.find((e) => e.startsWith("value") && e !== "value");
};

/**
 * Map a FHIR value field name (e.g. "valueCoding") to its TypeScript type.
 */
const VALUE_PREFIX_RE = /^value/;

export const valueFieldToTsType = (valueField: string): string => {
    const fhirName = valueField.replace(VALUE_PREFIX_RE, "");
    // Primitive types that map to TS primitives
    const primitives: Record<string, string> = {
        String: "string",
        Boolean: "boolean",
        Integer: "number",
        Decimal: "number",
        Date: "string",
        DateTime: "string",
        Time: "string",
        Instant: "string",
        Uri: "string",
        Url: "string",
        Canonical: "string",
        Code: "string",
        Oid: "string",
        Id: "string",
        Markdown: "string",
        UnsignedInt: "number",
        PositiveInt: "number",
        Uuid: "string",
        Base64Binary: "string",
    };
    return primitives[fhirName] ?? fhirName;
};

/**
 * Collect sub-extension "flat input" info from an extension profile's own
 * slice definitions on its `extension` field.
 */
export const collectSubExtensionSlices = (extProfile: SnapshotProfileTypeSchema): SubExtensionSliceInfo[] => {
    const extensionField = extProfile.fields.extension;
    const extensionSlicing = extProfile.slicing?.extension;
    if (!extensionField || isChoiceDeclarationField(extensionField) || !extensionSlicing?.slices) return [];
    const result: SubExtensionSliceInfo[] = [];
    for (const [sliceName, slice] of Object.entries(extensionSlicing.slices)) {
        const valueField = extractValueField(slice.elements);
        if (!valueField) continue;
        const tsType = valueFieldToTsType(valueField);
        const isArray = slice.max === undefined;
        const isRequired = slice.min !== undefined && slice.min >= 1;
        result.push({
            name: tsCamelCase(sliceName) || sliceName,
            url: sliceName,
            valueField,
            tsType,
            isArray,
            isRequired,
        });
    }
    return result;
};

/**
 * Resolve extension URL → extension profile class info (if the extension has
 * its own generated profile class in the index).
 */
export const resolveExtensionProfile = (
    tsIndex: TypeSchemaIndex,
    pkgName: string,
    url: string,
): ExtensionProfileInfo | undefined => {
    const schema = tsIndex.resolveByUrl(pkgName, url as CanonicalUrl);
    if (!schema || !isProfileTypeSchema(schema)) return undefined;
    // Only resolve extension profiles from the same package to avoid cross-package imports
    if (schema.identifier.package !== pkgName) return undefined;
    const snapshot = tsIndex.resolve(snapshotIdentifier(schema.identifier));
    if (!snapshot) return undefined;
    const className = tsProfileClassName(snapshot);
    const modulePath = `./${tsProfileModuleName(tsIndex, snapshot)}`;
    return { className, modulePath, snapshot };
};

/** Generate the body of a raw Extension branch: validate url, then push/upsert. */
const generateRawExtensionBody = (
    w: TypeScript,
    ext: ProfileExtension,
    targetPath: string[],
    paramName = "input",
    useUpsert = false,
) => {
    w.line(
        `if (${paramName}.url !== ${JSON.stringify(ext.url)}) throw new Error(\`Expected extension url '${ext.url}', got '\${${paramName}.url}'\`)`,
    );
    generateExtensionPush(w, targetPath, paramName, useUpsert);
};

/** Generate the code that pushes (or upserts) an extension onto the target (root or nested path). */
export const generateExtensionPush = (w: TypeScript, targetPath: string[], extExpr: string, useUpsert = false) => {
    const fn = useUpsert ? "upsertExtension" : "pushExtension";
    if (targetPath.length === 0) {
        w.line(`${fn}(this.resource, ${extExpr})`);
    } else {
        w.line(
            `const target = ensurePath(this.resource as unknown as Record<string, unknown>, ${JSON.stringify(targetPath)})`,
        );
        w.line("if (!Array.isArray(target.extension)) target.extension = [] as Extension[]");
        w.line(`${fn}(target as unknown as { extension?: Extension[] }, ${extExpr})`);
    }
};

/** Generate the extension lookup code for getters. */
const generateExtLookup = (w: TypeScript, ext: ProfileExtension, targetPath: string[]) => {
    if (targetPath.length === 0) {
        w.line(`const ext = this.resource.extension?.find(e => e.url === "${ext.url}")`);
    } else {
        w.line(
            `const target = ensurePath(this.resource as unknown as Record<string, unknown>, ${JSON.stringify(targetPath)})`,
        );
        w.line(`const ext = (target.extension as Extension[] | undefined)?.find(e => e.url === "${ext.url}")`);
    }
};

const effectiveGetterDefault = (w: TypeScript, hasProfile: boolean): "flat" | "profile" | "raw" => {
    const configured = w.opts.extensionGetterDefault ?? "flat";
    if (configured === "profile" && !hasProfile) return "flat";
    return configured;
};

const returnTypeForMode = (mode: "flat" | "profile" | "raw", inputType: string, profileClassName?: string): string => {
    if (mode === "profile" && profileClassName) return profileClassName;
    if (mode === "raw") return "Extension";
    return inputType;
};

const generateExtensionGetterOverloads = (
    w: TypeScript,
    ext: ProfileExtension,
    targetPath: string[],
    methodName: string,
    inputType: string,
    extProfileInfo: ExtensionProfileInfo | undefined,
    generateInputBody: () => void,
) => {
    const hasProfile = !!extProfileInfo;
    const defaultMode = effectiveGetterDefault(w, hasProfile);
    const modes: ("flat" | "profile" | "raw")[] = hasProfile ? ["flat", "profile", "raw"] : ["flat", "raw"];

    for (const mode of modes) {
        const rt = returnTypeForMode(mode, inputType, extProfileInfo?.className);
        w.lineSM(`public ${methodName}(mode: '${mode}'): ${rt} | undefined`);
    }
    const defaultReturn = returnTypeForMode(defaultMode, inputType, extProfileInfo?.className);
    w.lineSM(`public ${methodName}(): ${defaultReturn} | undefined`);

    const allReturns = [...new Set(modes.map((m) => returnTypeForMode(m, inputType, extProfileInfo?.className)))];
    const modesUnion = modes.map((m) => `'${m}'`).join(" | ");
    w.curlyBlock(
        ["public", methodName, `(mode: ${modesUnion} = '${defaultMode}'): ${allReturns.join(" | ")} | undefined`],
        () => {
            generateExtLookup(w, ext, targetPath);
            w.line("if (!ext) return undefined");
            w.line("if (mode === 'raw') return ext");
            if (hasProfile) {
                w.line(`if (mode === 'profile') return ${extProfileInfo?.className}.apply(ext)`);
            }
            generateInputBody();
        },
    );
};

type ExtensionMethodInfo = {
    ext: ProfileExtension;
    snapshot: SnapshotProfileTypeSchema;
    setMethodName: string;
    getMethodName: string;
    targetPath: string[];
    extProfileInfo: ExtensionProfileInfo | undefined;
};

// Complex extension — has sub-extensions (e.g., Race with ombCategory, detailed, text)

const generateComplexExtensionSetter = (w: TypeScript, info: ExtensionMethodInfo) => {
    const { ext, snapshot, setMethodName, targetPath, extProfileInfo } = info;
    const tsProfileName = tsResourceName(snapshot.identifier);
    const inputTypeName = tsExtensionFlatTypeName(tsProfileName, ext.name);
    const extProfileHasFlatInput = extProfileInfo
        ? collectSubExtensionSlices(extProfileInfo.snapshot).length > 0
        : false;
    const useUpsert = ext.max === "1";

    if (extProfileInfo && extProfileHasFlatInput) {
        const paramType = `${extProfileInfo.className}Flat | ${extProfileInfo.className} | Extension`;
        w.curlyBlock(["public", setMethodName, `(input: ${paramType}): this`], () => {
            w.ifElseChain(
                [
                    {
                        cond: `input instanceof ${extProfileInfo.className}`,
                        body: () => generateExtensionPush(w, targetPath, "input.toResource()", useUpsert),
                    },
                    {
                        cond: "isExtension<Extension>(input)",
                        body: () => generateRawExtensionBody(w, ext, targetPath, "input", useUpsert),
                    },
                ],
                () =>
                    generateExtensionPush(
                        w,
                        targetPath,
                        `${extProfileInfo.className}.createResource(input)`,
                        useUpsert,
                    ),
            );
            w.line("return this");
        });
    } else {
        w.curlyBlock(["public", setMethodName, `(input: ${inputTypeName}): this`], () => {
            w.line("const subExtensions: Extension[] = []");
            for (const sub of ext.subExtensions ?? []) {
                const valueField = sub.valueFieldType ? tsValueFieldName(sub.valueFieldType) : "value";
                if (sub.max === "*") {
                    w.curlyBlock(["if", `(input.${sub.name})`], () => {
                        w.curlyBlock(["for", `(const item of input.${sub.name})`], () => {
                            w.line(`subExtensions.push({ url: "${sub.url}", ${valueField}: item } as Extension)`);
                        });
                    });
                } else {
                    w.curlyBlock(["if", `(input.${sub.name} !== undefined)`], () => {
                        w.line(
                            `subExtensions.push({ url: "${sub.url}", ${valueField}: input.${sub.name} } as Extension)`,
                        );
                    });
                }
            }
            const extLiteral = `{ url: "${ext.url}", extension: subExtensions }`;
            const fn = useUpsert ? "upsertExtension" : "pushExtension";
            if (targetPath.length === 0) {
                w.line(`${fn}(this.resource, ${extLiteral})`);
            } else {
                w.line(
                    `const target = ensurePath(this.resource as unknown as Record<string, unknown>, ${JSON.stringify(targetPath)})`,
                );
                w.line("if (!Array.isArray(target.extension)) target.extension = [] as Extension[]");
                w.line(`${fn}(target as unknown as { extension?: Extension[] }, ${extLiteral})`);
            }
            w.line("return this");
        });
    }
};

const generateComplexExtensionGetter = (w: TypeScript, info: ExtensionMethodInfo) => {
    const { ext, snapshot, getMethodName, targetPath, extProfileInfo } = info;
    const tsProfileName = tsResourceName(snapshot.identifier);
    const inputTypeName = tsExtensionFlatTypeName(tsProfileName, ext.name);
    const extProfileHasFlatInput = extProfileInfo
        ? collectSubExtensionSlices(extProfileInfo.snapshot).length > 0
        : false;
    const inputType = extProfileHasFlatInput && extProfileInfo ? `${extProfileInfo.className}Flat` : inputTypeName;

    generateExtensionGetterOverloads(w, ext, targetPath, getMethodName, inputType, extProfileInfo, () => {
        const configItems = (ext.subExtensions ?? []).map((sub) => {
            const valueField = sub.valueFieldType ? tsValueFieldName(sub.valueFieldType) : "value";
            const isArray = sub.max === "*";
            return `{ name: "${sub.url}", valueField: "${valueField}", isArray: ${isArray} }`;
        });
        w.line(`const config = [${configItems.join(", ")}]`);
        w.line(`return extractComplexExtension<${inputType}>(ext, config)`);
    });
};

// Single-value extension — one known value type (e.g., birthSex with valueCode)

const generateSingleValueExtensionSetter = (w: TypeScript, tsIndex: TypeSchemaIndex, info: ExtensionMethodInfo) => {
    const { ext, setMethodName, targetPath, extProfileInfo } = info;
    const firstValueType = ext.valueFieldTypes?.[0];
    if (!firstValueType) return;
    const valueType = tsTypeFromIdentifier(firstValueType);
    const valueField = tsValueFieldName(firstValueType);
    const useUpsert = ext.max === "1";

    if (extProfileInfo) {
        const extFactoryInfo = collectProfileFactoryInfo(tsIndex, extProfileInfo.snapshot);
        const extValueParam = extFactoryInfo.params.find((p) => p.name === valueField);
        const resolvedValueType = extValueParam?.tsType ?? valueType;
        const paramType = `${extProfileInfo.className} | Extension | ${resolvedValueType}`;
        const elseExpr = extValueParam
            ? `${extProfileInfo.className}.createResource({ ${valueField}: value as ${resolvedValueType} })`
            : `{ url: "${ext.url}", ${valueField}: value as ${valueType} } as Extension`;
        w.curlyBlock(["public", setMethodName, `(value: ${paramType}): this`], () => {
            w.ifElseChain(
                [
                    {
                        cond: `value instanceof ${extProfileInfo.className}`,
                        body: () => generateExtensionPush(w, targetPath, "value.toResource()", useUpsert),
                    },
                    {
                        cond: "isExtension(value)",
                        body: () => generateRawExtensionBody(w, ext, targetPath, "value", useUpsert),
                    },
                ],
                () => generateExtensionPush(w, targetPath, elseExpr, useUpsert),
            );
            w.line("return this");
        });
    } else {
        w.curlyBlock(["public", setMethodName, `(value: ${valueType}): this`], () => {
            const extLiteral = `{ url: "${ext.url}", ${valueField}: value } as Extension`;
            generateExtensionPush(w, targetPath, extLiteral, useUpsert);
            w.line("return this");
        });
    }
};

const generateSingleValueExtensionGetter = (w: TypeScript, info: ExtensionMethodInfo) => {
    const { ext, getMethodName, targetPath, extProfileInfo } = info;
    const firstValueType = ext.valueFieldTypes?.[0];
    if (!firstValueType) return;
    const valueType = tsTypeFromIdentifier(firstValueType);
    const valueField = tsValueFieldName(firstValueType);

    generateExtensionGetterOverloads(w, ext, targetPath, getMethodName, valueType, extProfileInfo, () => {
        w.line(`return getExtensionValue<${valueType}>(ext, "${valueField}")`);
    });
};

// Generic extension — no known value type

const generateGenericExtensionSetter = (w: TypeScript, info: ExtensionMethodInfo) => {
    const { ext, setMethodName, targetPath } = info;
    const useUpsert = ext.max === "1";

    w.curlyBlock(["public", setMethodName, `(value: Omit<Extension, "url"> | Extension): this`], () => {
        w.ifElseChain(
            [
                {
                    cond: "isExtension(value)",
                    body: () => generateRawExtensionBody(w, ext, targetPath, "value", useUpsert),
                },
            ],
            () => generateExtensionPush(w, targetPath, `{ url: "${ext.url}", ...value } as Extension`, useUpsert),
        );
        w.line("return this");
    });
};

const generateGenericExtensionGetter = (w: TypeScript, info: ExtensionMethodInfo) => {
    const { ext, getMethodName, targetPath } = info;

    w.curlyBlock(["public", getMethodName, "(): Extension | undefined"], () => {
        if (targetPath.length === 0) {
            w.line(`return this.resource.extension?.find(e => e.url === "${ext.url}")`);
        } else {
            w.line(
                `const target = ensurePath(this.resource as unknown as Record<string, unknown>, ${JSON.stringify(targetPath)})`,
            );
            w.line(`return (target.extension as Extension[] | undefined)?.find(e => e.url === "${ext.url}")`);
        }
    });
};

export const generateExtensionMethods = (
    w: TypeScript,
    tsIndex: TypeSchemaIndex,
    snapshot: SnapshotProfileTypeSchema,
) => {
    for (const ext of snapshot.extensions ?? []) {
        if (!ext.url) continue;
        const baseName = ext.nameCandidates.recommended;
        const targetPath = ext.path.split(".").filter((segment) => segment !== "extension");
        const extProfileInfo = resolveExtensionProfile(tsIndex, snapshot.identifier.package, ext.url);
        const info: ExtensionMethodInfo = {
            ext,
            snapshot,
            setMethodName: `set${baseName}`,
            getMethodName: `get${baseName}`,
            targetPath,
            extProfileInfo,
        };

        if (ext.isComplex && ext.subExtensions) {
            generateComplexExtensionSetter(w, info);
            w.line();
            generateComplexExtensionGetter(w, info);
        } else if (ext.valueFieldTypes?.length === 1 && ext.valueFieldTypes[0]) {
            generateSingleValueExtensionSetter(w, tsIndex, info);
            w.line();
            generateSingleValueExtensionGetter(w, info);
        } else {
            generateGenericExtensionSetter(w, info);
            w.line();
            generateGenericExtensionGetter(w, info);
        }
        w.line();
    }
};

export const collectTypesFromExtensions = (
    tsIndex: TypeSchemaIndex,
    snapshot: SnapshotProfileTypeSchema,
    addType: (typeId: TypeIdentifier) => void,
): boolean => {
    let needsExtensionType = false;

    for (const ext of snapshot.extensions ?? []) {
        if (ext.isComplex && ext.subExtensions) {
            needsExtensionType = true;
            for (const sub of ext.subExtensions) {
                if (!sub.valueFieldType) continue;
                const resolvedType = tsIndex.resolveByUrl(
                    snapshot.identifier.package,
                    sub.valueFieldType.url as CanonicalUrl,
                );
                addType(resolvedType?.identifier ?? sub.valueFieldType);
            }
        } else if (ext.valueFieldTypes && ext.valueFieldTypes.length === 1) {
            needsExtensionType = true;
            if (ext.valueFieldTypes[0]) {
                const resolvedType = tsIndex.resolveByUrl(
                    snapshot.identifier.package,
                    ext.valueFieldTypes[0].url as CanonicalUrl,
                );
                addType(resolvedType?.identifier ?? ext.valueFieldTypes[0]);
            }
        } else {
            needsExtensionType = true;
        }
    }

    return needsExtensionType;
};

/** Collect types used in the FlatInput of extension profiles. */
export const collectTypesFromFlatInput = (
    tsIndex: TypeSchemaIndex,
    snapshot: SnapshotProfileTypeSchema,
    addType: (typeId: TypeIdentifier) => void,
) => {
    if (snapshot.base.name !== "Extension") return;
    const subSlices = collectSubExtensionSlices(snapshot);
    for (const sub of subSlices) {
        const tsType = sub.tsType;
        // Primitive types (string, boolean, number) don't need imports
        if (["string", "boolean", "number"].includes(tsType)) continue;
        // Resolve complex FHIR type by name
        const fhirUrl = `http://hl7.org/fhir/StructureDefinition/${tsType}` as CanonicalUrl;
        const schema = tsIndex.resolveByUrl(snapshot.identifier.package, fhirUrl);
        if (schema) addType(schema.identifier);
    }
};
